import { useState } from "react";
import Modal from "./Modal";
import type { Deal } from "../../../lib/types";
import { scheduleAdjusterMeeting } from "../../../lib/dispatchActions";
import { useAuth } from "../../../context/AuthContext";

interface Props {
  open: boolean;
  deal: Deal;
  onClose: () => void;
  onCompleted: () => void;
}

export default function ScheduleAdjusterModal({
  open,
  deal,
  onClose,
  onCompleted,
}: Props) {
  const { member } = useAuth();
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [adjusterName, setAdjusterName] = useState(deal.adjuster_name ?? "");
  const [adjusterPhone, setAdjusterPhone] = useState(deal.adjuster_phone ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isWeekend = (() => {
    if (!date) return false;
    const d = new Date(date + "T12:00:00");
    const day = d.getDay();
    return day === 0 || day === 6;
  })();

  async function submit() {
    setErr(null);
    if (!date) return setErr("Pick a date.");
    if (isWeekend) return setErr("Adjuster meetings cannot be scheduled on weekends.");
    if (!time) return setErr("Pick a start time.");
    setSubmitting(true);
    try {
      await scheduleAdjusterMeeting(
        deal,
        {
          date,
          time,
          adjusterName: adjusterName || undefined,
          adjusterPhone: adjusterPhone || undefined,
        },
        member?.id ?? null,
      );
      onCompleted();
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      title="Schedule adjuster meeting"
      subtitle={`${deal.homeowner_name ?? "(no name)"} · ${deal.property_street ?? ""}`}
      onClose={onClose}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="px-4 py-1.5 rounded bg-bardin-orange text-white text-sm disabled:opacity-60"
          >
            {submitting ? "Saving…" : "Schedule Meeting"}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Date
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
          />
          {isWeekend && (
            <p className="text-xs text-red-600 mt-1">No weekend scheduling — pick a weekday.</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Start time
          </label>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
          />
        </div>
      </div>
      <p className="text-xs text-slate-500 -mt-1">
        Insurance usually gives a window (e.g., 10am–1pm). Enter the <span className="font-medium">start</span> — adjusters typically arrive at that time. OK to update later if the adjuster confirms a tighter time the night before.
      </p>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Adjuster Name <span className="text-slate-400">(optional)</span>
        </label>
        <input
          type="text"
          value={adjusterName}
          onChange={(e) => setAdjusterName(e.target.value)}
          className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
          placeholder="e.g. John Smith"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Adjuster Phone <span className="text-slate-400">(optional)</span>
        </label>
        <input
          type="tel"
          value={adjusterPhone}
          onChange={(e) => setAdjusterPhone(e.target.value)}
          className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
          placeholder="(555) 555-5555"
        />
      </div>

      {deal.insurance_company && (
        <div className="text-xs text-slate-500 bg-slate-50 px-3 py-2 rounded">
          Insurance: <span className="font-medium">{deal.insurance_company}</span>
          {deal.claim_number && <> · Claim #{deal.claim_number}</>}
        </div>
      )}

      <p className="text-xs text-slate-500">
        Deal stays at <span className="font-medium">Adjuster Meeting Ready</span> — the meeting date is the flag. RS, RM, and Robert are notified. PRE should be done the day before.
      </p>

      {err && (
        <div className="bg-red-50 text-red-700 text-sm p-2 rounded">{err}</div>
      )}
    </Modal>
  );
}
